import { inject } from '@angular/core'
import { CanMatchFn, Router } from '@angular/router'
import { catchError, defaultIfEmpty, map, of, switchMap, take } from 'rxjs'
import { isFeatureAllowed } from '../config/feature-permission.config'
import { AuthService } from '../services/auth.service'
import { UserStore } from '../stores/user.store'

/**
 * AIロープレ低遅延経路(live)の canMatch ガード。
 *
 * org フラグ ROLEPLAY_LOW_LATENCY が有効なユーザーだけ streaming ページを
 * マッチさせる。false の場合、ルーターは同一 path の次のルート(旧ページ)へ
 * フォールスルーする。
 *
 * - canMatch は親ルートの authGuard(canActivate)より先に走るため、まず
 *   authService.user$ で Firebase 認証の復元を待つ(licenseGuard と同じ構成)。
 *   待たずに getOneSelf を投げると、対話画面のリロード直後にトークン無しの
 *   リクエストが UNAUTHENTICATED になり、フラグ ON でも旧ページへ落ちる
 * - 未ログインは false(旧ページ側にフォールスルーし、authGuard がログインへ流す)
 * - フラグ解決は ROLEPLAY_CAPTIONS と同じ getOneSelf(cache-first)→
 *   isFeatureAllowed パターン(PR1/SBL-1001)。解決失敗時は false = 旧経路
 * - `legacyRoleplay` クエリパラメータが付いている場合も false。
 *   サーバーが開始を拒否(枠不足・エンジン接続失敗等)したとき、streaming
 *   ページがこのパラメータを付けて同一 URL へ再遷移し旧経路で開始し直す
 */
export const roleplayStreamingGuard: CanMatchFn = () => {
  const router = inject(Router)
  const authService = inject(AuthService)
  const userStore = inject(UserStore)

  const queryParams =
    router.getCurrentNavigation()?.extractedUrl.queryParams ?? {}
  if (queryParams['legacyRoleplay']) {
    return false
  }

  return authService.user$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) {
        return of(false)
      }
      return userStore
        .getOneSelf({ useCache: true })
        .pipe(
          map((ctxUser) =>
            isFeatureAllowed('ROLEPLAY_LOW_LATENCY', ctxUser.enabledFeatures),
          ),
        )
    }),
    catchError(() => of(false)),
    // getOneSelf はエラー種別によっては値を流さず完了しうる(サービス層が
    // 非 CustomError を握って空完了に変換するため)。ガードが未発火のまま
    // 完了するとルーターのマッチングが解決しないので、空完了も false に落とす
    defaultIfEmpty(false),
  )
}
